import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Observable } from "rxjs";
import { HttpBody, HttpOptions, HttpOptionsConfig } from "../../networking/models/base-service/base-service";
import { isPlatformBrowser } from "@angular/common";
import { SourceEnum } from "../../networking/models/base-service/enum/base-service.enum";
import { countryConfig } from "src/country-config/country-config";

@Injectable({
  providedIn: 'root',
})
export class BaseService {

  MAX_WIDTH_RESPONSIVE: number = 991;

  isBrowser: boolean = false;
  defaultParams = {};
  initialOptions: HttpOptions = {};
  initialConfig: HttpOptionsConfig = {
    defaultHeaders: true,
    defaultParams: true,
  };

  get defaultHeaders(): HttpOptions["headers"] {
    return {
      'Content-Type': 'application/json',
      country: countryConfig.indicator,
      source: this.getSource(),
    };
  }

  get country(): string {
    return countryConfig.indicator;
  }

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) platformId,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get<T>(
    url: string,
    options: HttpOptions = this.initialOptions,
    config: HttpOptionsConfig = this.initialConfig
  ): Observable<T> {
    return this.http.get<T>(this.normalizeUrl(url), this.getOptions(options, config));
  }

  post<T>(
    url: string,
    body: HttpBody,
    options: HttpOptions = this.initialOptions,
    config: HttpOptionsConfig = this.initialConfig
  ): Observable<T> {
    return this.http.post<T>(this.normalizeUrl(url), body, this.getOptions(options, config));
  }

  put<T>(
    url: string,
    body: HttpBody,
    options: HttpOptions = this.initialOptions,
    config: HttpOptionsConfig = this.initialConfig
  ): Observable<T> {
    return this.http.put<T>(this.normalizeUrl(url), body, this.getOptions(options, config));
  }

  patch<T>(
    url: string,
    body: HttpBody,
    options: HttpOptions = this.initialOptions,
    config: HttpOptionsConfig = this.initialConfig
  ): Observable<T> {
    return this.http.patch<T>(this.normalizeUrl(url), body, this.getOptions(options, config));
  }

  delete<T>(
    url: string,
    options: HttpOptions = this.initialOptions,
    config: HttpOptionsConfig = this.initialConfig,
    body?: HttpBody
  ): Observable<T> {
    return this.http.delete<T>(this.normalizeUrl(url), this.getOptions(options, config, body));
  }

  getOptions(
    options: HttpOptions,
    config: HttpOptionsConfig,
    body: HttpBody = {}
  ): HttpOptions {
    let newHttpOptions: HttpOptions;
    let newHeaders = {};
    let newParams = {};

    if (config?.defaultHeaders) {
      newHeaders = { ...newHeaders, ...this.defaultHeaders };
    }

    if (config?.defaultParams) {
      newParams = { ...newParams, ...this.defaultParams };
    }

    if (options?.headers) {
      newHeaders = { ...newHeaders, ...options.headers };
    }

    if (options?.params) {
      newParams = { ...newParams, ...options.params };
    }

    newHttpOptions = {
      ...options,
      headers: newHeaders,
      params: newParams,
      body: body,
    };

    return newHttpOptions;
  }

  getSource(): SourceEnum {
    if (!this.isBrowser) { return SourceEnum.WEB; }

    return window?.screen?.width <= this.MAX_WIDTH_RESPONSIVE
      ? SourceEnum.RESPONSIVE
      : SourceEnum.WEB;
  }

  normalizeUrl(url: string): string {
    const country = this.country.slice(0, 2);
    return url.replace(':country', country);
  }
  
}
